import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Customer } from "@/types/customer";
import { Product } from "@/types/product";
import { User, ShieldBan, Package, Phone, MapPin, Briefcase, UserCheck, Pencil, FileText, Calendar } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EditSaleDialog } from "@/components/edit-sale-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

interface CustomerProfileDialogProps {
  customer: Customer | null;
  products: Product[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdateProduct: (product: Product) => Promise<void> | void;
}

export function CustomerProfileDialog({ customer, products, open, onOpenChange, onUpdateProduct }: CustomerProfileDialogProps) {
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editSaleOpen, setEditSaleOpen] = useState(false);

  if (!customer) return null;

  const customerProducts = products.filter(p => p.customerId === customer.id)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Statistics
  const totalPurchasesIQD = customerProducts.filter(p => p.currency === "IQD").reduce((acc, p) => acc + p.salePrice, 0);
  const totalPurchasesUSD = customerProducts.filter(p => p.currency === "USD").reduce((acc, p) => acc + p.salePrice, 0);
  const totalRemainingIQD = customerProducts.filter(p => p.currency === "IQD").reduce((acc, p) => acc + p.remainingAmount, 0);
  const totalRemainingUSD = customerProducts.filter(p => p.currency === "USD").reduce((acc, p) => acc + p.remainingAmount, 0);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 overflow-hidden">
          {/* Header Profile */}
          <div className="bg-primary/5 border-b p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center border-2 border-background shadow-sm">
                <span className="text-2xl font-bold text-primary">{customer.customerCode}</span>
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                  {customer.fullName}
                  {customer.isBlocked ? (
                    <Badge variant="destructive" className="text-xs">
                      <ShieldBan className="h-3 w-3 ml-1" />
                      بلۆک کراو
                    </Badge>
                  ) : (
                    <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-700">
                      <UserCheck className="h-3 w-3 ml-1" />
                      چالاک
                    </Badge>
                  )}
                </DialogTitle>
                <div className="text-muted-foreground text-sm flex items-center gap-2 mt-1">
                  <Phone className="h-3 w-3" />
                  <span dir="ltr">{customer.phone1}</span>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="flex gap-2 text-xs md:text-sm">
              <div className="bg-background rounded-lg p-2 border shadow-sm text-center min-w-[100px]">
                <div className="text-muted-foreground mb-1">کۆی کڕین</div>
                <div className="font-bold text-primary space-y-0.5">
                  {totalPurchasesUSD > 0 && <div dir="ltr">${totalPurchasesUSD.toLocaleString()}</div>}
                  {totalPurchasesIQD > 0 && <div><span dir="ltr">{totalPurchasesIQD.toLocaleString()}</span> <span className="text-[10px] font-normal">د.ع</span></div>}
                  {totalPurchasesUSD === 0 && totalPurchasesIQD === 0 && "0"}
                </div>
              </div>
              <div className="bg-background rounded-lg p-2 border shadow-sm text-center min-w-[100px]">
                <div className="text-muted-foreground mb-1">کۆی قەرز</div>
                <div className="font-bold text-red-600 space-y-0.5">
                  {totalRemainingUSD > 0 && <div dir="ltr">${totalRemainingUSD.toLocaleString()}</div>}
                  {totalRemainingIQD > 0 && <div><span dir="ltr">{totalRemainingIQD.toLocaleString()}</span> <span className="text-[10px] font-normal">د.ع</span></div>}
                  {totalRemainingUSD === 0 && totalRemainingIQD === 0 && "0"}
                </div>
              </div>
            </div>
          </div>

          <Tabs defaultValue="products" className="flex-1 flex flex-col overflow-hidden" dir="rtl">
            <div className="px-6 pt-4 border-b">
              <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
                <TabsTrigger value="products">کاڵا کڕاوەکان ({customerProducts.length})</TabsTrigger>
                <TabsTrigger value="info">زانیاری کەسی</TabsTrigger>
              </TabsList>
            </div>

            {/* Products Tab */}
            <TabsContent value="products" className="flex-1 overflow-auto p-0 m-0 bg-muted/5">
              {customerProducts.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground space-y-2">
                  <Package className="h-12 w-12 opacity-20" />
                  <p>هیچ کاڵایەک نەدۆزرایەوە</p>
                </div>
              ) : (
                <div className="p-4 md:p-6 space-y-4">
                  {/* Desktop Table */}
                  <div className="hidden md:block rounded-md border bg-background overflow-hidden">
                    <Table>
                      <TableHeader className="bg-muted/50">
                        <TableRow>
                          <TableHead className="text-right">کاڵا</TableHead>
                          <TableHead className="text-right">نرخ</TableHead>
                          <TableHead className="text-right">بڕی دراو</TableHead>
                          <TableHead className="text-right">ماوە</TableHead>
                          <TableHead className="text-right">بەروار</TableHead>
                          <TableHead className="text-right w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {customerProducts.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell className="font-medium">
                              <div>{product.deviceName}</div>
                              {product.deviceDescription && (
                                <div className="text-xs text-muted-foreground truncate max-w-[200px]">{product.deviceDescription}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 justify-start">
                                <span className="font-semibold" dir="ltr">{product.salePrice.toLocaleString()}</span>
                                <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">{product.currency === "IQD" ? "د.ع" : "$"}</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-green-600 font-medium">
                              <div className="flex items-center gap-1.5 justify-start">
                                <span dir="ltr">{product.paidAmount.toLocaleString()}</span>
                                <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">{product.currency === "IQD" ? "د.ع" : "$"}</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-red-500 font-medium">
                              <div className="flex items-center gap-1.5 justify-start">
                                <span dir="ltr">{product.remainingAmount.toLocaleString()}</span>
                                <Badge variant="destructive" className="text-[10px] px-1 py-0 h-4">{product.currency === "IQD" ? "د.ع" : "$"}</Badge>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm" dir="ltr">
                              {product.date}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 hover:bg-primary/10 hover:text-primary transition-colors"
                                onClick={() => {
                                  setEditingProduct(product);
                                  setEditSaleOpen(true);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-3">
                    {customerProducts.map((product) => (
                      <div key={product.id} className="bg-background rounded-xl border p-3 shadow-sm">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-semibold">{product.deviceName}</div>
                            {product.deviceDescription && (
                              <div className="text-xs text-muted-foreground">{product.deviceDescription}</div>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 -mt-1 -ml-2"
                            onClick={() => {
                              setEditingProduct(product);
                              setEditSaleOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-3 gap-2 text-xs bg-muted/30 rounded-lg p-2 mb-2">
                          <div className="text-center">
                            <div className="text-muted-foreground mb-1">نرخ</div>
                            <div className="font-medium" dir="ltr">
                              {product.salePrice.toLocaleString()} <span className="text-[10px]">{product.currency === "IQD" ? "د.ع" : "$"}</span>
                            </div>
                          </div>
                          <div className="text-center border-x border-dashed border-gray-300 dark:border-gray-700">
                            <div className="text-muted-foreground mb-1">دراو</div>
                            <div className="font-medium text-green-600" dir="ltr">
                              {product.paidAmount.toLocaleString()}
                            </div>
                          </div>
                          <div className="text-center">
                            <div className="text-muted-foreground mb-1">ماوە</div>
                            <div className="font-medium text-red-600" dir="ltr">
                              {product.remainingAmount.toLocaleString()}
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-center text-xs text-muted-foreground px-1">
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span dir="ltr">{product.date}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span>{product.installments} قیست</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Info Tab */}
            <TabsContent value="info" className="flex-1 overflow-auto p-4 md:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 border-b pb-2">
                    <User className="h-4 w-4" />
                    زانیاری کەسی
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">ناوی تەواو</span>
                      <span className="font-medium">{customer.fullName}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">کۆدی کڕیار</span>
                      <span className="font-medium font-mono text-primary">#{customer.customerCode}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">پیشە</span>
                      <span className="font-medium">{customer.job || "-"}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 border-b pb-2">
                    <Phone className="h-4 w-4" />
                    پەیوەندی و ناونیشان
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">مۆبایل ١</span>
                      <span className="font-medium" dir="ltr">{customer.phone1}</span>
                    </div>
                    {customer.phone2 && (
                      <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                        <span className="text-sm text-muted-foreground">مۆبایل ٢</span>
                        <span className="font-medium" dir="ltr">{customer.phone2}</span>
                      </div>
                    )}
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">ناونیشان</span>
                      <span className="font-medium">{customer.address || "-"}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 border-b pb-2">
                    <UserCheck className="h-4 w-4" />
                    زانیاری کەفیل
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">ناوی کەفیل</span>
                      <span className="font-medium">{customer.guarantorName || "-"}</span>
                    </div>
                    <div className="flex justify-between p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">ژمارەی کەفیل</span>
                      <span className="font-medium" dir="ltr">{customer.guarantorPhone || "-"}</span>
                    </div>
                  </div>
                </div>

                {customer.notes && (
                  <div className="space-y-4 md:col-span-2">
                    <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2 border-b pb-2">
                      <FileText className="h-4 w-4" />
                      تێبینی
                    </h3>
                    <div className="p-4 bg-yellow-50 dark:bg-yellow-950/20 text-yellow-800 dark:text-yellow-200 rounded-lg border border-yellow-200 dark:border-yellow-900 leading-relaxed">
                      {customer.notes}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>

          <div className="p-4 border-t bg-background">
            <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
              داخستن
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Sale Dialog Container to avoid nesting issues */}
      <EditSaleDialog
        product={editingProduct}
        open={editSaleOpen}
        onOpenChange={(open) => {
          setEditSaleOpen(open);
          if (!open) {
            setTimeout(() => setEditingProduct(null), 200);
          }
        }}
        onUpdate={onUpdateProduct}
      />
    </>
  );
}
